import React from 'react';
import { Box, Grid, Skeleton, Stack } from '@mui/material';

interface AppointmentModalSkeletonProps {
  /**
   * Number of grid rows to display
   * @default 4
   */
  rows?: number;
  /**
   * Number of columns per row
   * @default 3
   */
  columns?: number;
  /**
   * Show header section with title and actions
   * @default true
   */
  showHeader?: boolean;
  /**
   * Show divider section
   * @default true
   */
  showDivider?: boolean;
  /**
   * Show footer actions
   * @default true
   */
  showActions?: boolean;
  /**
   * Show additional info section (like check-in time)
   * @default true
   */
  showAdditionalInfo?: boolean;
}

const AppointmentModalSkeleton: React.FC<AppointmentModalSkeletonProps> = ({
  rows = 4,
  columns = 3,
  showHeader = true,
  showDivider = true,
  showActions = true,
  showAdditionalInfo = true,
}) => {
  // Generate random widths for more realistic skeleton
  const getRandomWidth = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const renderSkeletonField = (index: number) => (
    <Grid item xs={12} sm={6} md={4} key={index}>
      <Skeleton variant="text" width={getRandomWidth(60, 120)} height={20} />
      <Skeleton variant="text" width={getRandomWidth(80, 160)} height={24} />
    </Grid>
  );

  return (
    <>
      {/* Header skeleton */}
      {showHeader && (
        <Stack direction='row' justifyContent='space-between' mb={3}>
          <Skeleton variant="text" width={200} height={32} />
          <Stack direction='row' gap={3} alignItems="center">
            <Skeleton variant="rounded" width={100} height={24} />
            <Skeleton variant="circular" width={28} height={28} />
          </Stack>
        </Stack>
      )}

      {/* Content skeleton */}
      <Grid container spacing={3}>
        {Array.from({ length: rows * columns }, (_, index) => renderSkeletonField(index))}
      </Grid>

      {/* Divider skeleton */}
      {showDivider && (
        <Box mt={3} mb={3}>
          <Skeleton variant="rectangular" width="100%" height={1} />
        </Box>
      )}

      {/* Additional info skeleton (like check-in time) */}
      {showAdditionalInfo && (
        <Skeleton variant="text" width={150} height={20} sx={{ mb: 3 }} />
      )}

      {/* Action buttons skeleton */}
      {showActions && (
        <Stack direction='row' gap={6}>
          <Skeleton variant="rounded" width={100} height={24} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Stack>
      )}
    </>
  );
};

export default AppointmentModalSkeleton;